import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuthStore } from "@/store/useAuthStore";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Button from "../ui/button/Button";

function PinBoxes({
  pin,
  onPinChange,
  onPinKeyDown,
  onPaste,
  pinRefs,
  autoFocusFirst,
  testIdPrefix,
}: {
  pin: string[];
  onPinChange: (index: number, value: string) => void;
  onPinKeyDown: (index: number, e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  pinRefs: React.RefObject<HTMLInputElement | null>[];
  autoFocusFirst?: boolean;
  testIdPrefix?: string;
}) {
  useEffect(() => {
    if (autoFocusFirst) {
      pinRefs[0].current?.focus();
    }
  }, [autoFocusFirst]);

  return (
    <div className="flex gap-2 sm:gap-3" onPaste={onPaste}>
      {pin.map((digit, i) => (
        <input
          key={i}
          ref={pinRefs[i]}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => onPinChange(i, e.target.value)}
          onKeyDown={(e) => onPinKeyDown(i, e)}
          className="h-11 w-0 flex-1 rounded-lg border border-gray-300 bg-transparent text-center text-lg font-semibold text-gray-800 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-brand-500"
          autoComplete="off"
          data-testid={testIdPrefix ? `${testIdPrefix}-${i}` : undefined}
        />
      ))}
    </div>
  );
}

// Resend waits as long as the server keeps a just-sent code
// (reuseCodeWithin in backend/internal/service/otp_service.go). Pressed
// sooner, it sends no email — and a button that does nothing looks broken.
const RESEND_AFTER_MS = 30_000;

export default function SignInForm() {
  const navigate = useNavigate();
  const { login, verifyOTP, clearOTP, isLoading, error, otpRequired, otpToken, emailHint } =
    useAuthStore();

  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const pinRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const otpRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Focus first PIN box when phone is filled
  useEffect(() => {
    if (phone.length >= 10 && !otpRequired) {
      pinRefs[0].current?.focus();
    }
  }, [phone]);

  // Every successful login that needs a code hands back a fresh otp token, so
  // a new token is the moment a code went out — the first one included.
  const [codeSentAt, setCodeSentAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (otpToken) {
      const sent = Date.now();
      setCodeSentAt(sent);
      setNow(sent);
    }
  }, [otpToken]);
  const resendIn =
    codeSentAt === null ? 0 : Math.max(0, Math.ceil((codeSentAt + RESEND_AFTER_MS - now) / 1000));
  const counting = resendIn > 0;
  useEffect(() => {
    if (!counting) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [counting]);

  // Focus first OTP box when OTP screen appears
  useEffect(() => {
    if (otpRequired) {
      otpRefs[0].current?.focus();
    }
  }, [otpRequired]);

  const handleDigitChange = (
    digits: string[],
    setDigits: (d: string[]) => void,
    refs: React.RefObject<HTMLInputElement | null>[],
    index: number,
    value: string,
  ) => {
    if (value && !/^\d$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);
    setFormError("");
    if (value && index < 5) {
      refs[index + 1].current?.focus();
    }
  };

  const handleDigitKeyDown = (
    digits: string[],
    refs: React.RefObject<HTMLInputElement | null>[],
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      refs[index - 1].current?.focus();
    }
  };

  const handleDigitPaste = (
    setDigits: (d: string[]) => void,
    refs: React.RefObject<HTMLInputElement | null>[],
    e: React.ClipboardEvent,
  ) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(""));
      refs[5].current?.focus();
    }
  };

  const navigateAfterLogin = () => {
    const user = useAuthStore.getState().user;
    navigate(user?.role === "admin" ? "/system" : "/", { replace: true });
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (phone.length < 10) {
      setFormError("Enter a valid phone number");
      return;
    }

    const pinStr = pin.join("");
    if (pinStr.length !== 6) {
      setFormError("Enter all 6 digits of your PIN");
      return;
    }

    setSubmitting(true);
    try {
      await login(phone, pinStr);
      // If OTP is required, store will set otpRequired — don't navigate yet
      if (!useAuthStore.getState().otpRequired) {
        navigateAfterLogin();
      }
    } catch {
      // Error is set in the store
    } finally {
      setSubmitting(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    const otpStr = otp.join("");
    if (otpStr.length !== 6) {
      setFormError("Enter all 6 digits of the OTP");
      return;
    }

    setSubmitting(true);
    try {
      await verifyOTP(otpStr);
      navigateAfterLogin();
    } catch {
      // Error is set in the store
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    clearOTP();
    setOtp(["", "", "", "", "", ""]);
    setFormError("");
  };

  const handleResendOTP = async () => {
    setFormError("");
    setOtp(["", "", "", "", "", ""]);
    setSubmitting(true);
    try {
      await login(phone, pin.join(""));
    } catch {
      // Error is set in the store
    } finally {
      setSubmitting(false);
    }
  };

  const displayError = formError || error;
  const isDisabled = submitting || isLoading;

  // OTP verification screen
  if (otpRequired) {
    return (
      <>
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Verify OTP
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {emailHint
              ? `Enter the 6-digit code sent to ${emailHint}`
              : "Enter the 6-digit code sent to your email"}
          </p>
        </div>

        <form onSubmit={handleOTPSubmit} className="space-y-5">
          <div>
            <Label>6-Digit OTP</Label>
            <PinBoxes
              pin={otp}
              onPinChange={(i, v) => handleDigitChange(otp, setOtp, otpRefs, i, v)}
              onPinKeyDown={(i, e) => handleDigitKeyDown(otp, otpRefs, i, e)}
              onPaste={(e) => handleDigitPaste(setOtp, otpRefs, e)}
              pinRefs={otpRefs}
              autoFocusFirst
              testIdPrefix="otp"
            />
          </div>

          {displayError && (
            <div className="rounded-lg bg-error-50 px-4 py-3 dark:bg-error-500/10">
              <p className="text-sm text-error-600 dark:text-error-400">
                {displayError}
              </p>
            </div>
          )}

          <Button type="submit" className="w-full" size="md" disabled={isDisabled}>
            {isDisabled ? "Verifying..." : "Verify OTP"}
          </Button>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Back to login
            </button>
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={isDisabled || counting}
              className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:text-gray-400 dark:disabled:text-gray-500"
            >
              {counting ? `Resend OTP in ${resendIn}s` : "Resend OTP"}
            </button>
          </div>
        </form>
      </>
    );
  }

  // Login screen (phone + PIN)
  return (
    <>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Sign In
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Enter your phone number and PIN to continue
        </p>
      </div>

      <form onSubmit={handleLoginSubmit} className="space-y-5">
        {/* Phone */}
        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            inputMode="numeric"
            placeholder="Enter your phone number"
            value={phone}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 15);
              setPhone(val);
              setFormError("");
            }}
            autoFocus
            autoComplete="tel"
          />
        </div>

        {/* PIN */}
        <div>
          <Label>6-Digit PIN</Label>
          <PinBoxes
            pin={pin}
            onPinChange={(i, v) => handleDigitChange(pin, setPin, pinRefs, i, v)}
            onPinKeyDown={(i, e) => handleDigitKeyDown(pin, pinRefs, i, e)}
            onPaste={(e) => handleDigitPaste(setPin, pinRefs, e)}
            pinRefs={pinRefs}
            testIdPrefix="pin"
          />
        </div>

        {/* Error */}
        {displayError && (
          <div className="rounded-lg bg-error-50 px-4 py-3 dark:bg-error-500/10">
            <p className="text-sm text-error-600 dark:text-error-400">
              {displayError}
            </p>
          </div>
        )}

        {/* Submit */}
        <Button type="submit" className="w-full" size="md" disabled={isDisabled}>
          {isDisabled ? "Signing in..." : "Sign In"}
        </Button>
      </form>
    </>
  );
}
